<script setup lang="ts">
/**
 * MY — Extension of Time (Section 259(2), M32015) 문서-뷰 패널.
 * PresetMVP ExtensionDcrDoc + ExtensionNoticeDoc 이식 — DCR·Notice 2종 + flag/choice 토글.
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  labelize, typeOf, EXTENSION_OF_TIME_REQUIRED,
} from './extensionOfTime/extensionOfTimeFields';
import { EXTENSION_OF_TIME_DOC_CTX } from './extensionOfTime/extensionOfTimeContext';
import {
  applyEotRegisteredClause,
  buildExtensionOfTimeVariables,
  computeCirculationDeadlineFromFye,
  emptyExtensionOfTimeState,
  formatEotDateDisplay,
  resolveEotCompanyType,
  type ExtensionOfTimeState,
} from '../mappers/extensionOfTimeMapper';
import { formatMyAddressLine, composeMyAddressLine } from '@/shared/utils/malaysianAddress';
import { fetchEntityAddresses, formatMyCompanyRegNo } from '@/shared/composables/useEntityAddresses';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import ExtensionDcrDoc from './extensionOfTime/ExtensionDcrDoc.vue';
import ExtensionNoticeDoc from './extensionOfTime/ExtensionNoticeDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  appliedClause?: {
    clauseNumber: string;
    governingBasis: 'constitution' | 'articles' | 'third_schedule' | null;
    rawOption?: string;
    seq: number;
  } | null;
  pendingPick?: { value: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const state = reactive<ExtensionOfTimeState>(emptyExtensionOfTimeState());

const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function setFieldValue(key: string, val: string) {
  state.values[key] = typeOf(key) === 'address' ? formatMyAddressLine(val) : val;
}

const articleNoOptions = ref<string[]>([]);

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (key === 'constitution_clause_number' || t === 'clause') {
    emit('request-person-lookup', {
      fieldGroup: 'constitution-clause',
      key: 'constitution_clause_number',
      clauseOptions: articleNoOptions.value,
      defaultValue: state.values.constitution_clause_number || '',
      defaultBasis: state.choices.resolution_basis || 'constitution',
    });
    inputKey.value = '';
    return;
  }
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', {
      fieldGroup: 'extension-of-time',
      key,
      defaultRole: null,
      lookupType: key === 'company_name' || t === 'entity' ? 'entity' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}

function applyClausePick(pick: {
  clauseNumber?: string | null;
  governingBasis?: string | null;
  rawOption?: string | null;
} | null | undefined) {
  if (!pick) return;
  const raw = String(pick.rawOption || '').trim();
  const fromRaw = raw ? applyEotRegisteredClause(raw) : null;
  const basis =
    (pick.governingBasis === 'constitution' || pick.governingBasis === 'articles' || pick.governingBasis === 'third_schedule'
      ? pick.governingBasis
      : null)
    || fromRaw?.basis
    || 'constitution';
  state.choices.resolution_basis = basis;
  if (basis === 'third_schedule') {
    state.values.constitution_clause_number = '';
    return;
  }
  const num = String(pick.clauseNumber || fromRaw?.clauseNo || '').trim();
  if (num) state.values.constitution_clause_number = num;
}

watch(
  () => props.appliedClause?.seq,
  () => { if (props.appliedClause) applyClausePick(props.appliedClause); },
);
watch(
  () => props.chatFormData?._clauseSyncAt,
  () => {
    if (!props.chatFormData) return;
    applyClausePick({
      clauseNumber: props.chatFormData.constitution_clause_number,
      governingBasis: props.chatFormData._resignGoverningBasis,
      rawOption: props.chatFormData._clauseRawOption,
    });
  },
);
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  state.values[key] = composeMyAddressLine(a);
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

function signersOf(varKey: string): SignerEntry[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: SignerEntry[]) {
  state.signers[varKey] = Array.isArray(list) ? list : [];
}

function flag(name: string): boolean {
  return !!state.flags[name];
}
function toggleFlag(name: string) {
  state.flags[name] = !state.flags[name];
}
function choiceOf(name: string): string {
  return state.choices[name] || '';
}
function setChoice(name: string, val: string) {
  state.choices[name] = val;
}

provide(EXTENSION_OF_TIME_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  entityCd,
  signersOf,
  setSigners,
  pendingPickValue,
  placePick,
  flag,
  toggleFlag,
  choiceOf,
  setChoice,
});

const entityPrefilled = ref('');

/** Entity 프로필에서 채워지는 키 — Change Entity 시 이전 회사 값 잔존 방지 */
const ENTITY_AUTOFILL_KEYS = [
  'company_name',
  'company_registration_no',
  'registered_office_address',
  'company_phone',
  'company_fax',
  'company_email',
  'constitution_clause_number',
  'financial_year_end',
  'circulation_deadline',
  'agm_deadline',
] as const;

function resetEntityAutofill() {
  for (const k of ENTITY_AUTOFILL_KEYS) state.values[k] = '';
  state.choices.company_type = 'private';
  state.choices.resolution_basis = 'constitution';
  state.flags.is_multiple_director_company = false;
  state.signers.dcr = [];
  state.signers.notice = [];
}

function toDirectorSigner(o: any, idSuffix: string): SignerEntry {
  const name = String(o?.name || o?.contactName || '').trim();
  const baseId = String(o?.id || o?.contactCd || o?.officerIdx || `dir-${name}`);
  return {
    id: `${baseId}-${idSuffix}`,
    name,
    title: String(o?.positionName || o?.position || 'Director').trim() || 'Director',
    role: 'director',
    email: String(o?.email || o?.contactEmail || '').trim(),
    contactCd: o?.contactCd ? String(o.contactCd) : undefined,
    officerIdx: o?.officerIdx != null ? Number(o.officerIdx) : undefined,
    showTitle: true,
    _signerLayout: 'box',
  };
}

function applyAllDirectorsAsSigners(directors: any[]) {
  const active = (directors || []).filter((d) => {
    if (String(d.cessationDate || d.CESSATION_DATE || d.dateOfCessation || '').trim()) return false;
    return !!String(d.name || d.contactName || '').trim();
  });
  state.flags.is_multiple_director_company = active.length > 1;
  state.signers.dcr = active.map((d) => toDirectorSigner(d, 'dcr'));
  state.signers.notice = active.map((d) => toDirectorSigner(d, 'notice'));
}

watch(
  () => [entityCd.value, props.entityData] as const,
  async ([cd]) => {
    if (!cd) {
      entityPrefilled.value = '';
      resetEntityAutofill();
      articleNoOptions.value = [];
      return;
    }
    if (entityPrefilled.value === cd) return;
    entityPrefilled.value = cd;
    resetEntityAutofill();

    /** force overwrite — 빈 값도 빈칸으로 반영 (이전 entity 잔존 금지) */
    const put = (k: string, v: any) => {
      state.values[k] = String(v ?? '').trim();
    };
    const putDate = (k: string, v: any) => {
      put(k, formatEotDateDisplay(v));
    };
    const putCompanyType = (codeOrName: unknown, name?: unknown) => {
      const raw = String(codeOrName || name || '').trim();
      if (!raw) {
        state.choices.company_type = 'private';
        return;
      }
      state.choices.company_type = resolveEotCompanyType(codeOrName, name);
    };
    const putRegisteredClause = (regClause: string) => {
      const applied = applyEotRegisteredClause(regClause);
      if (!applied) {
        state.choices.resolution_basis = 'constitution';
        state.values.constitution_clause_number = '';
        return;
      }
      state.choices.resolution_basis = applied.basis;
      state.values.constitution_clause_number =
        applied.basis === 'third_schedule' ? '' : applied.clauseNo;
    };
    const putCirculationFromFye = () => {
      const deadline = computeCirculationDeadlineFromFye(state.values.financial_year_end);
      state.values.circulation_deadline = deadline || '';
    };

    // 1) 목록/챗봇 entityData — 즉시 반영 (Private/Public · FYE 선반영)
    const ed = props.entityData || {};
    put('company_name', ed.companyName || ed.entityName || ed.COMPANY_NAME);
    put(
      'company_registration_no',
      formatMyCompanyRegNo(
        ed.registrationNo || ed.regNo || ed.REG_NO || ed.registrationNumber,
        ed.registrationNoOld || ed.regNoOld || ed.REG_NO_OLD,
      ),
    );
    putCompanyType(
      ed.companyTypeCode || ed.entityTypeCode || ed.COMPANY_TYPE || ed.companyType || ed.entityType,
      ed.companyTypeName || ed.entityTypeName || ed.companyType || ed.entityType,
    );
    putDate('financial_year_end', ed.financialYearEnd || ed.FINANCIAL_YEAR_END || ed.fiscalYearEnd);
    putDate('agm_deadline', ed.nextAGMDueDate || ed.agmDuedate || ed.AGM_DUEDATE || ed.agmDueDate);
    putCirculationFromFye();

    // 2) address-info(주소·등록번호) + directors + profile(유형·연락처·FYE) + 정관 옵션 병렬
    const [addrsSettled, dirsSettled, profileSettled, optsSettled] = await Promise.allSettled([
      fetchEntityAddresses(cd),
      fetch(`/api/documents/variables/directors/${encodeURIComponent(cd)}`, { credentials: 'include' })
        .then((x) => x.json()),
      fetch(`/api/entity_my/profile/${encodeURIComponent(cd)}`, { credentials: 'include' })
        .then(async (r) => (r.ok ? r.json() : null)),
      fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
        .then((x) => x.json()),
    ]);
    if (entityPrefilled.value !== cd) return;

    if (optsSettled.status === 'fulfilled' && optsSettled.value) {
      const optsJson = optsSettled.value;
      const opts =
        optsJson?.articleNoOptions ||
        optsJson?.data?.resolution?.articleNoOptions ||
        optsJson?.resolution?.articleNoOptions ||
        [];
      articleNoOptions.value = Array.isArray(opts)
        ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
        : [];
    } else {
      articleNoOptions.value = [];
    }

    if (addrsSettled.status === 'fulfilled' && addrsSettled.value) {
      const gov: any = (addrsSettled.value as any)?.government || addrsSettled.value;
      if (gov.companyName) put('company_name', gov.companyName);
      const regDisp = formatMyCompanyRegNo(gov.registrationNo || gov.regNo, gov.regNoOld || gov.registrationNoOld);
      if (regDisp) put('company_registration_no', regDisp);
      put('registered_office_address', gov.registered || gov.registeredOffice || gov.address || '');
      const regClause = String(gov.clauseNo || gov.articleNo || '').trim();
      if (regClause) putRegisteredClause(regClause);
    }

    if (profileSettled.status === 'fulfilled' && profileSettled.value) {
      const profile = profileSettled.value?.data || profileSettled.value?.profile || profileSettled.value || {};
      const registry = profile.registrySync || {};
      const accounting = profile.accountingDates || profile.accounting_dates || {};
      const alt = profile.alternateAddress || {};
      const branch0 = Array.isArray(profile.entityBranches) ? profile.entityBranches[0] : null;
      const regAddr = profile.registeredAddress || {};

      if (profile.companyName || registry.companyName) {
        put('company_name', profile.companyName || registry.companyName);
      }
      {
        const regDisp = formatMyCompanyRegNo(
          registry.regNo || registry.registrationNumber || profile.regNo || profile.registrationNo,
          registry.regNoOld || profile.regNoOld || profile.REG_NO_OLD || registry.REG_NO_OLD,
        );
        if (regDisp) put('company_registration_no', regDisp);
      }
      putCompanyType(
        registry.entityTypeCode || registry.COMPANY_TYPE || profile.companyType || profile.entityType,
        registry.entityType || profile.companyTypeName || profile.entityType,
      );
      putDate(
        'financial_year_end',
        accounting.financialYearEnd || accounting.fiscalYearEnd || profile.financialYearEnd || profile.FINANCIAL_YEAR_END,
      );
      putDate(
        'agm_deadline',
        accounting.nextAGMDueDate || accounting.agmDuedate || profile.nextAGMDueDate || profile.AGM_DUEDATE,
      );
      putCirculationFromFye();

      const tel =
        profile.companyTel || regAddr.tel || alt.tel || alt.phone || profile.companyPhone || profile.tel
        || (branch0 ? [branch0.telNo0, branch0.telNo1].filter(Boolean).join(' ') : '')
        || '';
      const fax =
        profile.companyFax || regAddr.fax || alt.fax || profile.fax
        || (branch0 ? [branch0.faxNo0, branch0.faxNo1].filter(Boolean).join(' ') : '')
        || '';
      const email =
        profile.companyEmail || regAddr.email || alt.email || profile.email || branch0?.email || '';
      put('company_phone', tel);
      put('company_fax', fax);
      put('company_email', email);

      if (!String(state.values.registered_office_address || '').trim() && regAddr) {
        const composed =
          typeof regAddr === 'string'
            ? regAddr
            : (regAddr.composed || regAddr.composedAddress || [
              regAddr.addressLine1, regAddr.addressLine2, regAddr.addressLine3,
              regAddr.postcode || regAddr.postalCode, regAddr.city, regAddr.state, regAddr.country,
            ].filter(Boolean).join(', '));
        put('registered_office_address', composed);
      }

      const article = String(profile.resolution?.articleNo || '').trim();
      if (article) putRegisteredClause(article);
    }

    if (dirsSettled.status === 'fulfilled') {
      const dr = dirsSettled.value;
      const directors: any[] = dr?.data?.directors || dr?.directors || [];
      applyAllDirectorsAsSigners(directors);
    }
  },
  { immediate: true },
);

const overallProgress = computed(() => {
  const list = EXTENSION_OF_TIME_REQUIRED;
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}

type DocKind = 'dcr' | 'notice';
const DOC_SPECS: { kind: DocKind; re: RegExp; label: string; signerKey: string }[] = [
  {
    kind: 'dcr',
    re: /circular\s*resolution|directors.?.*extension|extension.*circular|dcr/i,
    label: "Directors' Circular Resolution (Extension of Time)",
    signerKey: 'dcr',
  },
  {
    kind: 'notice',
    re: /259|notice\s*of\s*extension|extension\s*of\s*time|lodge\s*financial/i,
    label: 'Section 259(2) — Notice of Extension of Time',
    signerKey: 'notice',
  },
];

function resolveTemplates(): { tmpId: number; label: string; kind: DocKind; signerKey: string }[] {
  const docs = availableTemplates.value as any[];
  if (!docs.length) return [];
  const used = new Set<number>();
  const out: { tmpId: number; label: string; kind: DocKind; signerKey: string }[] = [];
  for (const spec of DOC_SPECS) {
    const hit = docs.find((d) => !used.has(d.tmpId) && spec.re.test(tmplName(d)));
    if (hit?.tmpId) {
      used.add(hit.tmpId);
      out.push({
        tmpId: hit.tmpId,
        label: templateFileLabel(hit, spec.label),
        kind: spec.kind,
        signerKey: spec.signerKey,
      });
    }
  }
  // 이름 매칭 실패 시 남은 템플릿을 순서대로 채움
  for (const d of docs) {
    if (used.has(d.tmpId) || !d.tmpId) continue;
    const fallback = DOC_SPECS.find((s) => !out.some((o) => o.kind === s.kind));
    if (!fallback) break;
    used.add(d.tmpId);
    out.push({
      tmpId: d.tmpId,
      label: templateFileLabel(d, fallback.label),
      kind: fallback.kind,
      signerKey: fallback.signerKey,
    });
  }
  return out;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }

  const hits = resolveTemplates();
  if (!hits.length) {
    progress.value = '⚠ 연결된 문서 템플릿이 없습니다. 관리자에서 Extension of Time 문서 템플릿을 연결해 주세요.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      companyName: state.values.company_name || '',
      companyRegistrationNo: state.values.company_registration_no || '',
      financialYearEnd: state.values.financial_year_end || '',
      extensionReason: state.values.extension_reason || '',
      dcrDate: state.values.dcr_date || '',
      declarationDate: state.values.declaration_date || '',
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing documents…';
    const formData = buildExtensionOfTimeVariables(state);
    const docsToSave = hits.map((h) => {
      const docSigners = state.signers[h.signerKey] || [];
      return {
        templateId: h.tmpId,
        label: h.label,
        personName: String(state.values.company_name || 'Company'),
        entityCd: entityCd.value || undefined,
        formData,
        signers: docSigners.length ? docSigners : undefined,
      };
    });
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[Extension of Time] document save failed:', err);
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[Extension of Time] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    financialYearEnd: state.values.financial_year_end || '',
    extensionReason: state.values.extension_reason || '',
    dcrDate: state.values.dcr_date || '',
    declarationDate: state.values.declaration_date || '',
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    const base = emptyExtensionOfTimeState();
    Object.assign(state, {
      ...base,
      ...src,
      values: { ...(src.values || {}) },
      flags: { ...base.flags, ...(src.flags || {}) },
      choices: { ...base.choices, ...(src.choices || {}) },
      signers: { ...(src.signers || {}) },
    });
    for (const k of Object.keys(state.values)) {
      if (typeOf(k) === 'address' && state.values[k]) {
        state.values[k] = formatMyAddressLine(state.values[k]);
      }
    }
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Extension of Time (Section 259(2))</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. Extension of Time — Tilkoblet FYE 2025" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <div class="eot-pkg">
        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">⚖️</span>
            <span class="nm">Directors' Circular Resolution (Extension of Time)</span>
            <span class="no">Doc 1 / 2</span>
          </div>
          <div class="pkg-paper">
            <ExtensionDcrDoc />
          </div>
        </div>

        <div class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">📄</span>
            <span class="nm">Section 259(2) — Notice of Extension of Time</span>
            <span class="no">Doc 2 / 2</span>
          </div>
          <div class="pkg-paper">
            <ExtensionNoticeDoc />
          </div>
        </div>
      </div>
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 12px 28px; }
.co-footer { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; gap:12px; }
.co-foot-right { display:flex; align-items:center; gap:12px; margin-left:auto; }
.co-progress { font-size:12px; color:#5b6472; } .co-progress.err { color:#c0392b; }
.eot-pkg { display:flex; flex-direction:column; gap:28px; width:100%; max-width:900px; margin:0 auto; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper {
  background: #fff; padding: 34px 38px;
  box-shadow: 0 4px 24px rgba(20, 30, 60, .12);
  font-family: Arial, 'Malgun Gothic', sans-serif;
  color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px;
}
</style>
